// Sandpack templates for React Web and React Native (web) projects.
// Each entry returns the data a <ProjectSandpack /> needs: template, files,
// customSetup, and the "primary" file the AI will write into by default.

export const REACT_WEB_STARTER = `import { useState } from "react";

export default function App() {
  const [count, setCount] = useState(0);
  const [theme, setTheme] = useState("brand"); // "brand" | "midnight"

  const palettes = {
    brand: "linear-gradient(135deg, #3a5bff, #8b5cf6, #ff2196)",
    midnight: "linear-gradient(135deg, #05060a, #1a1f3d, #3a5bff)",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: palettes[theme],
        fontFamily: "system-ui, -apple-system, sans-serif",
        color: "white",
        padding: 24,
        transition: "background 0.6s ease",
      }}
    >
      <main
        style={{
          width: "100%",
          maxWidth: 520,
          textAlign: "center",
          background: "rgba(0,0,0,0.35)",
          padding: "40px 32px",
          borderRadius: 24,
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
        }}
      >
        <h1 style={{ fontSize: 36, margin: 0, letterSpacing: -0.5 }}>
          Hello, React.
        </h1>
        <p style={{ opacity: 0.8, margin: "12px 0 28px", lineHeight: 1.5 }}>
          A starter React app, bundled and previewed live in your browser.
          Ask the AI to turn this into anything you want.
        </p>

        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => setCount(c => c + 1)}
            style={btnPrimary}
          >
            Clicked {count} times
          </button>
          <button
            onClick={() => setTheme(t => (t === "brand" ? "midnight" : "brand"))}
            style={btnGhost}
          >
            Toggle theme
          </button>
        </div>
      </main>
    </div>
  );
}

const btnPrimary = {
  background: "white",
  color: "#3a5bff",
  border: 0,
  padding: "12px 24px",
  borderRadius: 999,
  fontWeight: 700,
  cursor: "pointer",
  fontSize: 15,
};

const btnGhost = {
  background: "transparent",
  color: "white",
  border: "1px solid rgba(255,255,255,0.35)",
  padding: "12px 24px",
  borderRadius: 999,
  fontWeight: 600,
  cursor: "pointer",
  fontSize: 15,
};
`;

export const RN_STARTER = `import { useState } from "react";
import {
  View,
  Text,
  Pressable,
  ScrollView,
  StyleSheet,
} from "react-native";

const FEATURES = [
  { id: 1, title: "Live preview", body: "Edits re-bundle automatically in your browser." },
  { id: 2, title: "Pure RN core", body: "Uses View / Text / Pressable / ScrollView only." },
  { id: 3, title: "Open in Expo Snack", body: "Tap the toolbar button to test on your phone." },
];

export default function App() {
  const [taps, setTaps] = useState(0);

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.scroll}>
        <Text style={styles.eyebrow}>The Consensus A.I.</Text>
        <Text style={styles.h1}>Hello, React Native.</Text>
        <Text style={styles.sub}>
          A starter Expo-style app, rendered live in your browser via react-native-web.
        </Text>

        <Pressable
          onPress={() => setTaps(t => t + 1)}
          style={({ pressed }) => [styles.cta, pressed && { opacity: 0.85 }]}
        >
          <Text style={styles.ctaLabel}>Tapped {taps} times</Text>
        </Pressable>

        <View style={styles.list}>
          {FEATURES.map((f, index) => (
            <View
              key={f.id}
              style={[styles.card, index < FEATURES.length - 1 && { marginBottom: 12 }]}
            >
              <Text style={styles.cardTitle}>{f.title}</Text>
              <Text style={styles.cardBody}>{f.body}</Text>
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: "#05060a" },
  scroll: {
    padding: 28,
    paddingTop: 56,
    alignItems: "stretch",
  },
  eyebrow: {
    color: "#5d7bff",
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 2,
    textTransform: "uppercase",
    marginBottom: 12,
  },
  h1: {
    color: "white",
    fontSize: 30,
    fontWeight: "800",
    letterSpacing: -0.5,
    marginBottom: 12,
  },
  sub: {
    color: "rgba(255,255,255,0.65)",
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 16,
  },
  cta: {
    marginTop: 8,
    marginBottom: 16,
    backgroundColor: "#3a5bff",
    paddingVertical: 14,
    borderRadius: 14,
    alignItems: "center",
  },
  ctaLabel: { color: "white", fontWeight: "700", fontSize: 16 },
  list: { marginTop: 12 },
  card: {
    backgroundColor: "rgba(58,91,255,0.08)",
    borderColor: "rgba(58,91,255,0.25)",
    borderWidth: 1,
    padding: 16,
    borderRadius: 14,
  },
  cardTitle: { color: "white", fontWeight: "700", fontSize: 15, marginBottom: 4 },
  cardBody: { color: "rgba(255,255,255,0.65)", fontSize: 13, lineHeight: 19 },
});
`;

export const PROJECT_TEMPLATES = {
  "react-web": {
    label: "React Web",
    template: "react",
    primaryFile: "/App.js",
    files: {
      "/App.js": { code: REACT_WEB_STARTER },
    },
  },
  "react-native": {
    label: "React Native (Expo)",
    // We use a custom react-native-web setup so the app previews in the browser.
    template: undefined,
    primaryFile: "/App.js",
    customSetup: {
      dependencies: {
        react: "18.2.0",
        "react-dom": "18.2.0",
        "react-native-web": "0.19.10",
      },
    },
    files: {
      "/index.js": {
        code: `import { AppRegistry } from "react-native";
import App from "./App";

AppRegistry.registerComponent("App", () => App);
AppRegistry.runApplication("App", {
  rootTag: document.getElementById("root"),
});
`,
        hidden: true,
      },
      "/public/index.html": {
        code: `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=390, initial-scale=1" />
    <title>Consensus RN</title>
  </head>
  <body style="margin:0; background:#0a0c14; height:844px; overflow:auto;">
    <div id="root" style="display:flex; min-height:844px; width:100%;"></div>
  </body>
</html>`,
        hidden: true,
      },
      "/App.js": { code: RN_STARTER },
    },
  },
};
